/**
 * The judgements the V2 Pulse row makes about a pool, kept apart from the markup
 * so they can be read and tested without rendering anything.
 *
 * A tone is deliberately not a colour: the row maps it onto the theme's own
 * custom properties, so the same rule works in every appearance.
 */
export type Tone = 'quiet' | 'watch' | 'loud' | 'good' | 'bad'

/**
 * One ladder for every share-of-supply reading — dev, snipers, top ten,
 * insiders, bundlers — so a glance at the colour is the whole answer.
 */
export const shareTone = (percent: number | undefined): Tone => {
  if (percent === undefined || !Number.isFinite(percent)) {
    return 'quiet'
  }
  if (percent < 10) {
    return 'quiet'
  }
  if (percent < 20) {
    return 'watch'
  }
  return 'loud'
}

/** A pair still inside its first five minutes is the one worth looking at. */
export const ageTone = (ageSeconds: number | undefined): Tone =>
  ageSeconds !== undefined && Number.isFinite(ageSeconds) && ageSeconds < 300
    ? 'good'
    : 'quiet'

/** Net bought, price change: the sign is the reading. */
export const signedTone = (value: number | undefined): Tone => {
  if (value === undefined || !Number.isFinite(value) || value === 0) {
    return 'quiet'
  }
  return value > 0 ? 'good' : 'bad'
}

export type Crown = { text: string; tone: Tone }

/**
 * How many of a creator's launches ever graduated. A first launch says nothing;
 * a long record with nothing to show for it says a great deal.
 */
export const crown = (
  launched: number | undefined,
  graduated: number | undefined,
): Crown | undefined => {
  if (!launched || !Number.isFinite(launched) || launched <= 0) {
    return undefined
  }

  const migrated =
    graduated !== undefined && Number.isFinite(graduated) && graduated > 0
      ? graduated
      : 0
  const text = `${migrated}/${launched}`

  if (launched === 1) {
    return { text, tone: 'quiet' }
  }
  if (migrated === 0 && launched >= 5) {
    return { text, tone: 'loud' }
  }
  if (migrated / launched >= 0.1) {
    return { text, tone: 'good' }
  }
  return { text, tone: 'watch' }
}

/**
 * The bonding curve, drawn as the ring around the token's art. Anything past
 * 85% is close enough to graduation to be worth a different colour.
 */
export const bondingTone = (percent: number | undefined): Tone => {
  if (percent === undefined || !Number.isFinite(percent) || percent <= 0) {
    return 'quiet'
  }
  return percent > 85 ? 'watch' : 'good'
}

/** Clamped so a stale or over-reported percentage cannot draw past a full turn. */
export const bondingTurn = (percent: number | undefined): number => {
  if (percent === undefined || !Number.isFinite(percent) || percent <= 0) {
    return 0
  }
  return Math.min(100, percent) / 100
}

/**
 * What the row shows where the price change normally goes.
 *
 * A reader who holds the pair cares about their own number, not the market's,
 * so the position takes the slot — but only when there is a position worth
 * reporting. A closed one reads as zero, and zero is not news: it would push
 * the market's figure out and say nothing in its place.
 */
export type ChangeSlot =
  | { kind: 'position'; value: number }
  | { kind: 'market'; value: number | undefined }

export const changeSlot = (
  positionPnlUsd: number | undefined,
  priceChange: number | undefined,
): ChangeSlot => {
  if (positionPnlUsd !== undefined && positionPnlUsd !== 0) {
    return { kind: 'position', value: positionPnlUsd }
  }
  return { kind: 'market', value: priceChange }
}

/**
 * Which mark the row carries down its side, when both could apply.
 *
 * A held position wins: it is the reason to find the row again while scrolling
 * past sixty that are not held, and it outlasts the two seconds an arrival is
 * worth. The arrival still shows as the wash across the row, so nothing is
 * lost by letting the position keep the edge.
 */
export type EdgeMark = 'held' | 'landed' | undefined

export const edgeMark = (held: boolean, landed: boolean): EdgeMark => {
  if (held) {
    return 'held'
  }
  return landed ? 'landed' : undefined
}
